import type React from "react";
import {
  forwardRef,
  useImperativeHandle,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";

import { LayoutContext } from "./context/layoutcontext";
import AppSidebar from "./AppSidebar";
import ProfileDropdown from "./ProfileDropdown";
import { getEasybillRateLimitUsage } from "../services/clearing/easybill";

const POLL_INTERVAL = 30_000;

const AppTopbar = forwardRef(
  (props: { sidebarRef: React.RefObject<HTMLDivElement> }, ref) => {
    const menubuttonRef = useRef(null);
    const { onMenuToggle, isDesktop } = useContext(LayoutContext);
    const navigate = useNavigate();

    const [easybillUsage, setEasybillUsage] = useState<number | null>(null);

    const fetchUsage = useCallback(async () => {
      try {
        const data = await getEasybillRateLimitUsage();
        const usage = Number(data?.requestsInLastMinute);
        setEasybillUsage(Number.isFinite(usage) ? usage : null);
      } catch {
        setEasybillUsage(null);
      }
    }, []);

    useEffect(() => {
      fetchUsage();
      const interval = setInterval(fetchUsage, POLL_INTERVAL);
      return () => clearInterval(interval);
    }, [fetchUsage]);

    useImperativeHandle(ref, () => ({
      menubutton: menubuttonRef.current,
    }));

    return (
      <div className="layout-topbar">
        <div className="topbar-left">
          <button
            ref={menubuttonRef}
            type="button"
            className="menu-button p-link"
            onClick={(e) => {
              isDesktop() ? navigate(-1) : onMenuToggle(e);
            }}
          >
            <i className="pi pi-chevron-left"></i>
          </button>

          <span className="topbar-separator"></span>
        </div>
        <div className="layout-topbar-menu-section">
          <AppSidebar sidebarRef={props.sidebarRef} />
        </div>

        <div className="topbar-right" style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <span
            title="Easybill API requests in the last minute"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
              fontSize: "0.85rem",
              color:
                easybillUsage === null
                  ? "#adb5bd"
                  : easybillUsage > 50
                    ? "#e74c3c"
                    : easybillUsage > 30
                      ? "#f39c12"
                      : "#6c757d",
              fontWeight: easybillUsage !== null && easybillUsage > 50 ? 700 : 500,
              whiteSpace: "nowrap",
            }}
          >
            <i className="pi pi-bolt" />
            {easybillUsage !== null ? `${easybillUsage}/min` : "--/min"}
          </span>
          <ProfileDropdown />
        </div>

        <div className="layout-mask modal-in"></div>
      </div>
    );
  }
);

export default AppTopbar;

AppTopbar.displayName = "AppTopbar";
